import { PlayerPublic } from "../../models/Player";
import PlayerPicture from "./PlayerPicture";
import "./Player.scss";
import classNames from 'classnames';
import Emoji from "../Emoji/Emoji";
import store, { RootState } from "../../store/store";
import { useAppSelector } from "../../hooks/redux";
import React, { useCallback, useEffect } from "react";
import Stats from "./Stats";
import { formatNumber } from "../../utils/formatting";
import { gameActions } from "../Game/gameSlice";
import { GameStatus } from "../../models/GameStatus";
import useGlobalBodyClick, { MULTITOOLTIP_CLASS } from "../../hooks/useGlobalBodyClick";

export default function Player(props: {
  player: Partial<PlayerPublic>,
  size?: number,
  statsVisible?: boolean,
  classes?: any,
  isPending?: boolean,
  isLastPending?: boolean,
  isClient?: boolean,
  place?: number,
  emoji?: string
}) {
  const {
    player,
    size = 50,
    statsVisible = true,
    classes = '',
    isPending = false,
    isLastPending = false,
    isClient = false,
    place = null,
    emoji = null
  } = props;

  const gameState = store.getState().game;

  const [statsDetailTooltip, setStatsDetailTooltip] = React.useState(false);
  const [statsClasses, setStatsClasses] = React.useState('');
  const [isAvatarClickEnabled, setAvatarClickEnabled] = React.useState(true);

  const keyPressed = useCallback((event) => {
    switch (event.key) {
      case 'Escape':
        setStatsDetailTooltip(false);
        break;
    }
  }, []);

  useGlobalBodyClick((target: Element) => {
    if (statsDetailTooltip) {

      if (target && (target as Element).classList.contains(MULTITOOLTIP_CLASS)) {
        return;
      }

      setStatsDetailTooltip(false);
    }
  });

  useEffect(() => {
    document.addEventListener("keydown", keyPressed, false);

    return () => {
      document.removeEventListener("keydown", keyPressed, false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emojiState = useAppSelector((state: RootState) => state.emoji);
  const playerEmoji = emojiState.emojis.find(it => it.playerId === player.playerId);

  const classnames = classNames({
    'player': true,
    'player_hidden-stats': !statsVisible,
    'player_pending': isPending,
    'player_client': isClient && !isLastPending,
    'player_client_last': isClient && isLastPending,
  }, classes);

  function statsClick(e: any, isVisible: boolean = false) {
    setStatsDetailTooltip(isVisible);

    const screenHeightArea = window.innerHeight / 3;

    if (gameState.status === GameStatus.GAME_SENTENCE_VOTE) {
      setStatsClasses('vertical-middle-aligned');
      return;
    }

    if (e.pageY < screenHeightArea) {
      // top area
      setStatsClasses('');
      return;
    }

    if (e.pageY > screenHeightArea * 2) {
      // bottom area
      setStatsClasses('bottom-aligned');
      return;
    }

    // middle area
    setStatsClasses('vertical-middle-aligned');
  }

  function avatarClick() {
    if (player && isAvatarClickEnabled && gameState.isConnected) {
      store.dispatch(gameActions.changeAvatar({
        gameId: gameState.gameId,
        playerId: player?.playerId || '',
      }));

      setAvatarClickEnabled(false);

      // allow to change avatar after some timeout
      setTimeout(() => {
        setAvatarClickEnabled(true);
      }, 200)
    }
  }

  const closeTooltipButton  = (
    <div onClick={() => setStatsDetailTooltip(false)} className="close_button">×</div>
  )

  return (
    <div className={classnames}>
      <div className="player_profile">
        {gameState.gameStarted &&
          <Emoji
            size={size}
            isClient={isClient}
            player={player}
            emoji={playerEmoji?.emoji}
          />
        }
        {isClient && !gameState.gameStarted &&
          <div className={`player_avatar-wrapper ${isAvatarClickEnabled ? 'click_enabled' : ''}`} style={{ width: size, height: size }} onClick={() => avatarClick()}></div>
        }
        {place && <div className="player_place">{place}</div>}
        <PlayerPicture player={player} size={size} />
        <div className="player_content">

          <div className="player_name">
            {player.username}
            {player.isConnected !== undefined &&
              <span className={`player_connection-status ${player.isConnected ? 'connected' : 'disconnected'}`}>●</span>
            }
            {emoji && <div className="player_emoji">{emoji}</div>}

          </div>
          {statsVisible &&
            <div className="player_stats">
              <div className="player_stats_score-minor">G: {formatNumber(player.guessCount)}</div>
              <div className="player_stats_score-minor">M: {formatNumber(player.matchCount)}</div>
            </div>
          }
        </div>
        {statsVisible &&
          <>
            <div className="player_score multitooltip" onClick={(e) => statsClick(e, !statsDetailTooltip)}>{formatNumber(player.score)}</div>
            {statsDetailTooltip &&
              <div /* onClick={() => setStatsDetailTooltip(false)} */>
                <Stats player={player} classes={statsClasses} closeButton={closeTooltipButton} />
              </div>
            }
          </>
        }
      </div>

    </div>
  )
}